import { Controller } from "@hotwired/stimulus"
import i18n from "../javascripts/util/i18n"

export default class extends Controller {
  static values = {
    currentStep: { type: Number, default: 1 },
    verified: { type: Boolean, default: false },
    // Translated strings from the server. Step 1 used to report in hardcoded
    // English while step 2 reported in German (#66).
    messages: { type: Object, default: {} },
    vatCheckRequired: { type: Boolean, default: false },
    vatFormats: { type: Object, default: {} },
    fieldFormats: { type: Object, default: {} }
  }

  static targets = [
    "password",
    "passwordConfirmation",
    "passwordError",
    "email",
    "emailError",
    "firstName",
    "firstNameError",
    "lastName",
    "lastNameError",
    "otpHidden",
    "codeInput",
    "phoneDisplay",
    "otpServerError",
    "otpMessage",
    "verifyError",
    "captchaContainer"
  ]

  connect() {
    console.log("Signup controller connected!");

    // Before anything can overwrite it — the label is translated and carries an
    // arrow icon, and the send-OTP path swaps in a spinner.
    const nextButton = document.querySelector('#step2-content button[data-action*="nextStep"]')
    if (nextButton) this.nextButtonHtml = nextButton.innerHTML

    this.goToStep(this.currentStepValue)

    // Re-render of step 3 (e.g. after a wrong-OTP server response) lands
    // here with the per-digit boxes possibly auto-restored by the browser.
    // Wipe them so the user starts the code entry fresh.
    if (this.currentStepValue === 3) {
      this.clearCodeInputs()
    }

    // If the server re-rendered after the OTP+captcha were already verified
    // (e.g. a save-time field error), the session proof still holds — submit
    // straight through instead of re-verifying a now-consumed OTP.
    this._verified = this.verifiedValue

    // A line of the server's error box goes once its field is edited, the box with its last line: a corrected
    // VAT ID still showed the old error (QA).
    this._onFieldInput = (event) => {
      const field = event.target.name?.match(/^user\[(\w+)\]$/)?.[1]
      if (!field) return
      this.element.querySelectorAll(`[data-step-errors] li[data-error-field="${field}"]`).forEach((line) => line.remove())
      this.element.querySelectorAll("[data-step-errors]").forEach((box) => { if (!box.querySelector("li")) box.remove() })
    }
    this.element.addEventListener("input", this._onFieldInput)

    // Intercept the final submit: verify OTP + captcha in the background first
    // so errors show inline without reloading (which would blank the password).
    this.form = this.element.querySelector('form')
    if (this.form) {
      this._onSubmit = this.handleSubmit.bind(this)
      this.form.addEventListener('submit', this._onSubmit)
    }

    this._companyFieldListeners = []
    this.setupCompanyFieldListeners()
  }

  disconnect() {
    if (this._onFieldInput) this.element.removeEventListener("input", this._onFieldInput)
    if (this.form && this._onSubmit) {
      this.form.removeEventListener('submit', this._onSubmit)
    }
    this.teardownCompanyFieldListeners()
  }
  
  goToStep(stepNumber) {
    // Hide all content
    document.querySelectorAll('.step-content').forEach(content => {
      content.classList.add('hidden');
    });
    
    // Show selected step content
    document.getElementById(`step${stepNumber}-content`).classList.remove('hidden');
    
    // Update sidebar indicators
    this.updateStepIndicators(stepNumber);
    this.currentStepValue = stepNumber;

    console.log(`Current step ${stepNumber}`);
    
    
    // Scroll to top on mobile
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  updateStepIndicators(activeStep) {
    for (let i = 1; i <= 3; i++) {
      const icon = document.getElementById(`step${i}-icon`);
      const status = document.getElementById(`step${i}-status`);
      
      if (i < activeStep) {
        // Completed step - EXACTLY like original
        icon.className = 'w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0';
        icon.querySelector('i').classList.remove('text-ps-orange');
        status.textContent = this.msg("step_complete");
        status.className = 'text-lg text-gray-400';
      } else if (i === activeStep) {
        // Active step - EXACTLY like original
        icon.className = 'w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0';
        icon.querySelector('i').classList.add('text-ps-orange');
        status.textContent = this.msg("step_in_progress");
        status.className = 'text-lg text-ps-orange ';
      } else {
        // Pending step - EXACTLY like original
        icon.className = 'w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0';
        icon.querySelector('i').classList.remove('text-ps-orange');
        status.textContent = this.msg("step_pending");
        status.className = 'text-lg text-gray-400';
      }
    }
  }

  // Auto-advance on the `input` event. It fires for real typing but NOT for the
  // programmatic value writes in handlePaste, so a full-code paste no longer
  // double-advances (the old keyup handler stepped forward once per paste-combo
  // keyup — "v" then "Control" — landing focus on the 3rd box). Using `input`
  // also works on mobile keyboards that report keyup as "Unidentified".
  handleCodeInput(event) {
    const input = event.target;
    // A wrong code clears the boxes but used to leave "that code is not
    // correct" on screen, so the customer retyped underneath a complaint about
    // a code they had already deleted. The message goes as soon as they start
    // the next attempt.
    this.clearOtpErrors()
    if (input.value.length === 1) {
      const next = input.nextElementSibling;
      if (next && next.tagName === 'INPUT') {
        next.focus();
      }
    }
    this.updateHiddenOtp()
  }

  // Both places a code problem can be reported: the inline verify message and
  // the step-3 server-error slot.
  clearOtpErrors() {
    this.hideVerifyError()
    if (this.hasOtpServerErrorTarget) {
      this.otpServerErrorTarget.classList.add("hidden")
    }
  }

  // Backspace on an already-empty box steps back to the previous one.
  handleBackspace(event) {
    if (event.key !== 'Backspace') return;
    const input = event.target;
    if (input.value.length === 0) {
      const prev = input.previousElementSibling;
      if (prev && prev.tagName === 'INPUT') {
        prev.focus();
        prev.select();
      }
    }
  }

  handlePaste(event) {
    event.preventDefault();
    // Pasting a code is a fresh attempt too, so the previous complaint goes.
    this.clearOtpErrors()
    // Keep digits only (a pasted code may carry spaces or a trailing newline).
    const digits = (event.clipboardData.getData('text') || '').replace(/\D/g, '');
    const inputs = event.target.parentElement.querySelectorAll('input');
    const count = Math.min(digits.length, inputs.length);

    for (let i = 0; i < count; i++) {
      inputs[i].value = digits[i];
    }

    // Focus the box after the last pasted digit, or the last box when full.
    const focusIndex = Math.min(count, inputs.length - 1);
    inputs[focusIndex]?.focus();
    this.updateHiddenOtp()
  }

  async nextStep() {
    if (this.currentStepValue === 1) {
      if (!this.validateStepOne()) return
      // Pre-flight: bounce the user back to step 1 with a clear error
      // if the email is already registered, instead of letting them
      // fill the entire form and learn at the very end.
      const emailOk = await this.checkEmailAvailable()
      if (!emailOk) return
    }
    if (this.currentStepValue === 2 && !this.validateCompany()) return

    if (this.currentStepValue < 3) {
      if (this.currentStepValue === 2) {
        this.sendOTP()
      } else {
        this.goToStep(this.currentStepValue + 1);
      }
    }
  }

  async checkEmailAvailable() {
    const email = (this.emailTarget?.value || "").trim()
    if (!email) return false

    try {
      const response = await fetch("/check_email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ email })
      })
      const data = await response.json()

      if (data.ok) {
        if (this.hasEmailErrorTarget) {
          this.emailErrorTarget.textContent = ""
          this.emailErrorTarget.classList.add("hidden")
        }
        this.emailTarget?.classList.remove("border-red-500")
        return true
      }

      if (this.hasEmailErrorTarget) {
        this.emailErrorTarget.textContent = data.message || this.msg("email_unavailable")
        this.emailErrorTarget.classList.remove("hidden")
      }
      this.emailTarget?.classList.add("border-red-500")
      this.emailTarget?.focus()
      return false
    } catch (e) {
      console.error("Email availability check failed:", e)
      // Network failure shouldn't block signup — the server-side
      // uniqueness validation on User#email will still catch a real
      // duplicate at the very end.
      return true
    }
  }

  async sendOTP() {
    const mobileField = document.querySelector('[name="user[mobile]"]')
    const phone = mobileField?.value?.trim()

    // A new code is about to be issued — any digits left over from a
    // previous attempt (initial send or resend) are now stale.
    this.clearCodeInputs()

    // Get the Next button and show loading state
    const nextButton = document.querySelector('#step2-content button[data-action*="nextStep"]')
    if (nextButton) {
      nextButton.disabled = true
      nextButton.innerHTML = '<svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> ' + i18n.t("js.signup.sending_code")
    }

    if (!phone) {
      if (mobileField) {
        mobileField.classList.add("border-red-500")
        const error = document.querySelector("[data-company-error='user[mobile]']")
        if (error) error.classList.remove("hidden")
      }
      this.resetNextButton()
      return
    }
    
    try {
      // show phone in verification step immediately (optimistic)
      if (this.hasPhoneDisplayTarget) this.phoneDisplayTarget.textContent = phone
    } catch (e) { /* ignore */ }

    try {
      const response = await fetch("/send_otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ phone })
      })

      const data = await response.json()

      if (data.success) {
        // A fresh code was issued: clear any stale server-rendered error (e.g. a
        // prior "Invalid OTP") and show the localized "code sent" confirmation,
        // so a resend never leaves the invalid-OTP message on screen.
        if (this.hasOtpServerErrorTarget) this.otpServerErrorTarget.classList.add("hidden")
        if (this.hasOtpMessageTarget) {
          this.otpMessageTarget.textContent = data.message || ""
          this.otpMessageTarget.classList.toggle("hidden", !data.message)
        }

        // Development convenience only — the server sends `dev_otp` in
        // development alone. Rendered inline rather than thrown into a dialog.
        if (data.dev_otp && this.hasOtpMessageTarget) {
          this.otpMessageTarget.textContent = `${data.message || ""} (${data.dev_otp})`.trim()
          this.otpMessageTarget.classList.remove("hidden")
        }
        // if server returns a formatted phone / masked value, show it
        if (data.phone) {
          if (this.hasPhoneDisplayTarget) this.phoneDisplayTarget.textContent = data.phone
        } else {
          if (this.hasPhoneDisplayTarget) this.phoneDisplayTarget.textContent = phone
        }
        this.goToStep(3)
      } else {
        // The server message is already localized; the generic fallback is too.
        this.showOtpError(data.message || this.msg("otp_send_failed"))
      }
    } catch (error) {
      console.error("Error sending OTP:", error)
      this.showOtpError(this.msg("otp_error"))
    } finally {
      // Always restore the Next button — on success the step transition has
      // already hidden it, so the brief reset is invisible; on failure the
      // user needs to fix the phone and click again.
      this.resetNextButton()
    }
  }

  // resend wrapper (called from view)
  resendOTP(event) {
    event?.preventDefault()
    this.sendOTP()
  }

  updateHiddenOtp() {
    if (!this.hasOtpHiddenTarget) return
    if (!this.hasCodeInputTarget) return
    const code = this.codeInputTargets.map(i => i.value || '').join('').slice(0,6)
    this.otpHiddenTarget.value = code
    return code
  }

  clearCodeInputs() {
    if (this.hasCodeInputTarget) {
      this.codeInputTargets.forEach(input => { input.value = '' })
    }
    if (this.hasOtpHiddenTarget) {
      this.otpHiddenTarget.value = ''
    }
  }

  handleSubmit(event) {
    // A server re-render never sends the password back, so after any error the
    // step-1 fields are empty. Ask for it again instead of posting a blank one.
    if (this.hasPasswordTarget && this.passwordTarget.value === "") {
      event.preventDefault()
      this.askForPasswordAgain()
      return
    }

    // Already verified (server re-render still holds the proof) → real POST.
    if (this._verified) return

    event.preventDefault()

    const code = this.updateHiddenOtp()
    if (!code || code.length < 6) {
      this.showVerifyError(this.msg("otp_incomplete"))
      return
    }

    this.verifyAndSubmit()
  }

  askForPasswordAgain() {
    this.goToStep(1)
    if (this.hasPasswordErrorTarget) {
      this.passwordErrorTarget.textContent = this.msg("password_reenter")
      this.passwordErrorTarget.classList.remove("hidden")
    }
    this.passwordTarget.focus()
    this.passwordTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  async verifyAndSubmit() {
    const submitBtn = document.querySelector('#step3-content button[type="submit"]')
    if (submitBtn) submitBtn.disabled = true
    this.hideVerifyError()

    const mobile = document.querySelector('[name="user[mobile]"]')?.value?.trim() || ''
    const otp = this.hasOtpHiddenTarget ? this.otpHiddenTarget.value : ''
    const captchaAnswer = this.form.querySelector('[name="captcha_answer"]')?.value || ''
    const captchaToken = this.form.querySelector('[name="captcha_token"]')?.value || ''

    try {
      const response = await fetch('/signup/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          user: { mobile, otp },
          captcha_answer: captchaAnswer,
          captcha_token: captchaToken
        })
      })
      const data = await response.json()

      if (data.ok) {
        // OTP + captcha accepted; submit the real form. Native submit() bypasses
        // this handler, so it POSTs straight to create (which trusts the proof).
        this._verified = true
        this.form.submit()
        return
      }

      this.showVerifyError(data.message || this.msg("verification_failed"))
      // Captcha is single-use per attempt — swap in the fresh challenge markup.
      if (data.captcha_html && this.hasCaptchaContainerTarget) {
        this.captchaContainerTarget.innerHTML = data.captcha_html
      }
      if (data.field === "otp") {
        this.clearCodeInputs()
        if (this.hasCodeInputTarget) this.codeInputTargets[0]?.focus()
      }
    } catch (e) {
      console.error("Signup verification failed:", e)
      this.showVerifyError(this.msg("verification_failed"))
    } finally {
      if (submitBtn) submitBtn.disabled = false
    }
  }

  showVerifyError(message) {
    if (!this.hasVerifyErrorTarget) {
      // No inline home for the message: log it rather than opening a dialog.
      console.error("[signup] verification error:", message)
      return
    }

    this.verifyErrorTarget.textContent = message
    this.verifyErrorTarget.classList.remove("hidden")
  }

  hideVerifyError() {
    if (this.hasVerifyErrorTarget) {
      this.verifyErrorTarget.textContent = ""
      this.verifyErrorTarget.classList.add("hidden")
    }
  }

  prevStep() {
    if (this.currentStepValue > 1) {
      this.resetNextButton()
      this.goToStep(this.currentStepValue - 1);
    }
  }

  // Optional: If you want to navigate to specific steps directly
  goToStep1() { this.goToStep(1); }
  goToStep2() { this.goToStep(2); }
  goToStep3() { this.goToStep(3); }

  // Translated string by key, with the key itself as a last-resort fallback so
  // a missing translation is visible rather than silently blank.
  msg(key) {
    return this.messagesValue?.[key] || key
  }

  // Shows an OTP/mobile problem where the customer is looking — beside the
  // field and in the page's own styling — instead of a native alert() (#66).
  showOtpError(message) {
    const mobileField = document.querySelector('[name="user[mobile]"]')
    if (mobileField) mobileField.classList.add("border-red-500")

    // The step-3 slot exists in the DOM even while step 2 is showing, so
    // "has the target" is not the same as "the customer can see it".
    const onScreen = (el) => el && el.offsetParent !== null

    const step3Slot = this.hasOtpServerErrorTarget ? this.otpServerErrorTarget : null
    const step2Slot = document.querySelector("[data-company-error='user[mobile]']")
    const slot = onScreen(step3Slot) ? step3Slot : (step2Slot || step3Slot)
    if (!slot) return

    slot.textContent = message
    slot.classList.remove("hidden")
    slot.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  resetNextButton() {
    const nextButton = document.querySelector('#step2-content button[data-action*="nextStep"]')
    if (!nextButton) return

    nextButton.disabled = false
    // Restore the markup captured in connect(), rather than overwriting it with
    // a literal: the label is translated and carries an arrow icon, both of
    // which a plain textContent assignment destroyed (#66).
    if (this.nextButtonHtml !== undefined) nextButton.innerHTML = this.nextButtonHtml
  }

  validateStepOne() {
    const okEmail = this.validateEmail()
    const okFirst = this.validateFirstName()
    const okLast = this.validateLastName()
    const okPwd = this.validatePasswords()
    return okEmail && okFirst && okLast && okPwd
  }

  // Same rule as the server (EmailAddress::TYPED).
  emailRegex() {
    return new RegExp(this.fieldFormatsValue.email || "^[^@\\s]+@[^@\\s.]+(\\.[^@\\s.]+)+$")
  }

  validateEmail() {
    const email = (this.emailTarget?.value || "").trim()
    const regex = this.emailRegex()
    let error = ""
    if (email.length === 0) {
      error = this.msg("email_required")
    } else if (!regex.test(email)) {
      error = this.msg("email_invalid")
    }
    if (this.hasEmailErrorTarget) {
      this.emailErrorTarget.textContent = error
      this.emailErrorTarget.classList.toggle("hidden", error === "")
    }
    if (this.emailTarget) {
      this.emailTarget.classList.toggle("border-red-500", error !== "")
    }
    return error === ""
  }

  validateFirstName() {
    return this.validatePersonalName("firstName", "first_name_required")
  }

  // The surname used to pass step 1 silently while a missing first name blocked
  // it — the same section, two different rules (#65).
  validateLastName() {
    return this.validatePersonalName("lastName", "last_name_required")
  }

  validatePersonalName(target, messageKey) {
    const field = this[`has${target[0].toUpperCase()}${target.slice(1)}Target`]
      ? this[`${target}Target`]
      : null
    const errorEl = this[`has${target[0].toUpperCase()}${target.slice(1)}ErrorTarget`]
      ? this[`${target}ErrorTarget`]
      : null

    const missing = (field?.value || "").trim().length === 0
    if (errorEl) {
      errorEl.textContent = missing ? this.msg(messageKey) : ""
      errorEl.classList.toggle("hidden", !missing)
    }
    if (field) field.classList.toggle("border-red-500", missing)
    return !missing
  }

  validatePasswords() {
    const pwd = (this.passwordTarget?.value || "").trim()
    const confirm = (this.passwordConfirmationTarget?.value || "").trim()
    let error = ""

    if (pwd.length < 12) {
      error = this.msg("password_too_short")
    } else if (pwd !== confirm) {
      error = this.msg("password_mismatch")
    }

    if (this.hasPasswordErrorTarget) {
      this.passwordErrorTarget.textContent = error
      this.passwordErrorTarget.classList.toggle("hidden", error === "")
    }

    return error === ""
  }

  // Live, as-you-type match check. Only manages the "do not match" message so it
  // never clobbers the length error that `validatePasswords` shows on Next.
  checkPasswordsMatch() {
    if (!this.hasPasswordErrorTarget) return

    const pwd = this.passwordTarget?.value || ""
    const confirm = this.passwordConfirmationTarget?.value || ""
    const mismatch = confirm.length > 0 && pwd !== confirm
    const el = this.passwordErrorTarget

    if (mismatch) {
      el.textContent = this.msg("password_mismatch")
      el.classList.remove("hidden")
    } else if (el.textContent === this.msg("password_mismatch")) {
      el.classList.add("hidden")
    }
  }

  validateCompany() {
    const container = document.getElementById("company-step")
    if (!container) return true

    let valid = true
    const required = container.querySelectorAll(".company-required")
    required.forEach((field) => {
      const error = container.querySelector(`[data-company-error='${field.name}']`)
      let fieldValid = field.value && field.value.trim() !== ""
      let customError = ""

      // additional email format check for invoice_email
      if (field.name === "user[invoice_email]" && fieldValid) {
        if (!this.emailRegex().test(field.value.trim())) {
          fieldValid = false
          customError = this.msg("invoice_email_invalid")
        }
      }

      // Format checks that only the server used to make, so "abcdefg" as a
      // telephone and "ABCDE!!" as a postal code passed the form and came back
      // as a server error after submit. The patterns are LegalEntity's own, passed
      // in by the page, so the two can't drift apart.
      if (fieldValid) {
        const pattern = this.fieldFormatsValue[field.name]
        if (pattern && !new RegExp(pattern).test(field.value.trim())) {
          fieldValid = false
          customError = this.msg(field.name === "user[postal_code]" ? "postal_code_invalid" : "phone_invalid")
        }
      }

      if (!fieldValid) {
        valid = false
        field.classList.add("border-red-500")
        if (error) {
          if (customError) error.textContent = customError
          error.classList.remove("hidden")
        }
      } else {
        field.classList.remove("border-red-500")
        if (error) error.classList.add("hidden")
      }
    })

    return this.validateVatId(container) && valid
  }

  // VAT id is required client-side only when RegistrationConfig#vat_check_required says so (#65). A given one must
  // match its country's pattern, the server's own (LegalEntity::VAT_ID_FORMATS): only the final submit checked
  // it, after the SMS code, and its error stayed on screen once the number was corrected (QA).
  validateVatId(container) {
    const field = container.querySelector('[name="user[vat_id]"]')
    if (!field) return true

    const error = container.querySelector("[data-company-error='user[vat_id]']")
    const value = (field.value || "").trim()
    const missing = this.vatCheckRequiredValue && value === ""
    const invalid = value !== "" && !this.vatIdLooksValid(value)

    field.classList.toggle("border-red-500", missing || invalid)
    if (error) {
      error.textContent = this.msg(invalid ? "vat_id_invalid" : "vat_id_required")
      error.classList.toggle("hidden", !(missing || invalid))
    }

    return !missing && !invalid
  }

  // Same reading as the server: separators ignored, case ignored, the first two letters pick the country.
  vatIdLooksValid(value) {
    const candidate = value.toUpperCase().replace(/[\s.\-]/g, "")
    const pattern = this.vatFormatsValue[candidate.slice(0, 2)]
    return Boolean(pattern) && new RegExp(pattern).test(candidate.slice(2))
  }

  setupCompanyFieldListeners() {
    const container = document.getElementById("company-step")
    if (!container) return

    const vatField = container.querySelector('[name="user[vat_id]"]')
    if (vatField) {
      const clearVatError = () => {
        vatField.classList.remove("border-red-500")
        const error = container.querySelector("[data-company-error='user[vat_id]']")
        if (error) error.classList.add("hidden")
      }
      vatField.addEventListener("input", clearVatError)
      vatField.addEventListener("focus", clearVatError)
      this._companyFieldListeners.push({ field: vatField, inputHandler: clearVatError, focusHandler: clearVatError })
    }

    container.querySelectorAll(".company-required").forEach((field) => {
      const inputHandler = () => {
        const error = container.querySelector(`[data-company-error='${field.name}']`)
        if (field.value && field.value.trim() !== "") {
          field.classList.remove("border-red-500")
          if (error) error.classList.add("hidden")
        }
      }
      const focusHandler = () => {
        field.classList.remove("border-red-500")
        const error = container.querySelector(`[data-company-error='${field.name}']`)
        if (error) error.classList.add("hidden")
      }

      field.addEventListener("input", inputHandler)
      field.addEventListener("focus", focusHandler)
      this._companyFieldListeners.push({ field, inputHandler, focusHandler })
    })
  }

  teardownCompanyFieldListeners() {
    if (!this._companyFieldListeners) return
    this._companyFieldListeners.forEach(({ field, inputHandler, focusHandler }) => {
      field.removeEventListener("input", inputHandler)
      field.removeEventListener("focus", focusHandler)
    })
    this._companyFieldListeners = []
  }
}
